const STORAGE_KEY = "snake-arena-online-features-v1";

interface OnlineFeaturesState {
  /** Whether the ONLINE FEATURES prompt has ever been answered (ENABLE or PLAY OFFLINE), so it's only ever shown once. */
  hasAsked: boolean;
  enabled: boolean;
}

function defaultState(): OnlineFeaturesState {
  return { hasAsked: false, enabled: false };
}

function isValid(value: unknown): value is OnlineFeaturesState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<OnlineFeaturesState>;
  return typeof candidate.hasAsked === "boolean" && typeof candidate.enabled === "boolean";
}

/**
 * The player's ONLINE FEATURES choice (leaderboards + anonymous gameplay
 * stats): whether they've been asked, and whether they opted in. Read
 * before any Supabase call is ever made - `OfflineLeaderboardService` and
 * `AuthService` both gate on `isEnabled()` first, so choosing PLAY
 * OFFLINE (or never being asked yet) means zero network calls, not just
 * a UI that hides them.
 */
export const OnlineFeaturesStorage = {
  get(): OnlineFeaturesState {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultState();
    } catch {
      return defaultState();
    }
  },

  save(state: OnlineFeaturesState): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures - online features simply won't persist
      // across a reload, which is not worth blocking play over.
    }
  },

  hasAsked(): boolean {
    return this.get().hasAsked;
  },

  isEnabled(): boolean {
    return this.get().enabled;
  },

  /** Records the player's answer to the ONLINE FEATURES prompt (or a later change via LEADERBOARD/Settings). */
  setEnabled(enabled: boolean): void {
    this.save({ hasAsked: true, enabled });
  },
};
